import { FC, useState, useCallback, useEffect, ReactNode } from 'react';
import { Form } from 'react-final-form';

import { readCarInfo } from 'src/api/plan';
import { Loader } from 'src/components/Loader/Loader';
import { Panel } from 'src/components/Panel/Panel';
import { Section } from 'src/components/Section/Section';
import { messagesActions } from 'src/store/messages/slice';
import { bookParking } from 'src/store/parking/services';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { ParkingBookingData } from 'src/types';
import { defaultServerError } from 'src/utils/consts';

import { BookActions } from './BookActions';
import { BookingData } from './BookingData';
import { CarFields } from './CarFields';
import classes from './Fields.module.scss';
import { NeedPassCheckbox } from './NeedPassCheckbox';
import { TimeInterval } from './TimeInterval';

export interface RegularBookingFieldsProps {
  status: ParkingBookingData | null | undefined;
  guest: boolean;
}

export const RegularBookingFields: FC<RegularBookingFieldsProps> = ({
  guest,
  status,
}) => {
  return (
    <>
      <BookingData guest={guest} />
      <TimeInterval status={status} />

      <Section
        title='Данные для бронирования'
        testId='parkingDataSection'
        contentClassName={classes.fields}
      >
        <CarFields />
        <NeedPassCheckbox />
      </Section>
    </>
  );
};

export interface RegularBookingFormValues {
  model: string;
  number: string;
  needPass: boolean;
}

export interface RegularBookingProps {
  employeeNumber: string;
  guest: boolean;
  status: ParkingBookingData | null | undefined;
}

export const RegularBooking: FC<RegularBookingProps> = ({
  employeeNumber,
  guest,
  status,
}) => {
  const dispatch = useAppDispatch();

  const [initialValues, setInitialValues] =
    useState<RegularBookingFormValues | null>(null);

  const init = useCallback(async () => {
    try {
      const carInfo = await readCarInfo(employeeNumber);
      setInitialValues({
        model: carInfo.model ?? '',
        number: carInfo.number ?? '',
        needPass: false,
      });
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
    }
  }, [dispatch, employeeNumber]);

  useEffect(() => {
    init();
  }, [init]);

  const book = useCallback(
    async (values: RegularBookingFormValues) => {
      return dispatch(
        bookParking({
          responsible: employeeNumber,
          carModel: values.model,
          carNumber: values.number,
          needPass: values.needPass,
        }),
      ).unwrap();
    },
    [dispatch, employeeNumber],
  );

  return initialValues ? (
    <Form onSubmit={book} initialValues={initialValues}>
      {() => (
        <Panel actions={<BookActions />}>
          <RegularBookingFields guest={guest} status={status} />
        </Panel>
      )}
    </Form>
  ) : (
    <Loader />
  );
};
